// US-ROUTINE-07 -- Routine history and completion streaks.

import {
  type RoutineEntry,
  type RoutineInstanceEntry,
  type RoutineStreaks,
  routineHistory,
  routineStreaks,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  task: RoutineEntry;
  currency: string;
  onBack: () => void;
};

const STATUS_LABELS: Record<string, string> = {
  approved: 'Approved',
  rejected: 'Not approved',
  missed: 'Missed',
  pending_guardian: 'Pending',
  pending_child: 'Pending',
};

const STATUS_COLOUR: Record<string, string> = {
  approved: 'var(--color-success, #1d6131)',
  rejected: 'var(--color-danger, #c0392b)',
  missed: 'var(--ink-3)',
  pending_guardian: 'var(--color-accent)',
  pending_child: 'var(--color-accent)',
};

export function formatRoutineScheduledDate(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00`));
}

export const RoutineHistoryScreen: Component<Props> = (props) => {
  const [history] = createResource(async () => {
    const r = await routineHistory(props.sessionId, props.task.recurringTaskId, 60);
    return r.ok ? r.value : [];
  });

  const [streaks] = createResource(async () => {
    const r = await routineStreaks(props.sessionId, props.task.recurringTaskId);
    return r.ok ? r.value : null;
  });

  const formatMinor = (minor: number) => (minor / 100).toFixed(2);

  return (
    <ActionScreen eyebrow="Routine history" title={props.task.taskName} onBack={props.onBack}>
      {/* Streak cards */}
      <Show when={streaks()}>
        {(s: () => RoutineStreaks) => (
          <div class="routine-streak-row">
            <div class="routine-streak-card">
              <span class="routine-streak-number">{s().currentStreak}</span>
              <span class="routine-streak-label">Current streak</span>
            </div>
            <div class="routine-streak-card routine-streak-card--best">
              <span class="routine-streak-number">{s().bestStreak}</span>
              <span class="routine-streak-label">Best ever</span>
            </div>
          </div>
        )}
      </Show>

      <Show when={history.loading}>
        <p style="padding: var(--space-4); color: var(--ink-2);">Loading...</p>
      </Show>

      <Show when={!history.loading && (history()?.length ?? 0) === 0}>
        <p style="padding: var(--space-4); color: var(--ink-2);">No history yet.</p>
      </Show>

      <ul class="history-list action-history-list">
        <For each={history() ?? []}>
          {(entry: RoutineInstanceEntry) => (
            <li class="history-row">
              <div class="history-row-leading">
                <span
                  class="history-amount"
                  style={`color: ${STATUS_COLOUR[entry.status] ?? 'var(--ink)'}`}
                >
                  {STATUS_LABELS[entry.status] ?? entry.status}
                </span>
                <Show when={entry.rewardMinorUnits > 0 && entry.status === 'approved'}>
                  <span style="font-size:0.8rem; color:var(--ink-2);">
                    +{formatMinor(entry.rewardMinorUnits)}
                  </span>
                </Show>
              </div>
              <div class="history-row-body">
                <span class="history-row-title">
                  {formatRoutineScheduledDate(entry.scheduledDate)}
                </span>
                <Show when={entry.rejectionReason}>
                  <span class="history-row-note">{entry.rejectionReason}</span>
                </Show>
              </div>
            </li>
          )}
        </For>
      </ul>
    </ActionScreen>
  );
};
